// src/pages/PracticePage.tsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Divider,
  Autocomplete,
  TextField,
  Paper,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { RichTreeView } from '@mui/x-tree-view';
import TuneIcon from '@mui/icons-material/Tune';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, markingDb } from '../../supabaseClient.ts';
import RichTextWithDefinitions from '../../components/RichTextWithDefinitions.tsx';
import RichText from '../../components/RichText.tsx';
import QuestionActionButtons from '../../components/QuestionActionButtons.tsx';
import { Question } from './types.ts';
import AnswerBox from './AnswerBox.tsx';
import { useTutorSession } from '../../context/TutorSessionContext.tsx';
import { useEffectiveStudentId } from '../../hooks/useEffectiveStudentId.ts';
import { useHasCompany } from '../../hooks/useHasCompany.ts';
import { logTutorSessionEvent } from '../../utils/tutorSessionEvents.ts';
// Reuse the same input component used in tests
import QuestionInput from '../TestPage/questions/Input.tsx';
import WorkingAnswerInput from './WorkingAnswerInput.tsx';
import { evaluateWorkingAnswerFeedback } from '../../utils/workingStepFeedback.ts';
import { areMathAnswersEquivalent } from '../../utils/mathAnswer.ts';
import {
  clientScoresMarkingType,
  isAiMarkingType,
  isDeterministicMarkingType,
  resolveMarkingType,
} from '../../utils/markingType.ts';
import { scoreQuestionSubmission, toSubmittableQuestion } from '../../utils/markSubmission.ts';
import { queryQuestionsWithMarkingFallback, PRACTICE_PART_SELECT, PRACTICE_SOLO_SELECT, fetchOpenFlaggedIds } from '../../utils/questionSelect.ts';
import { invokeShadowMarkAiSubmission, invokeShadowMarkPracticeAnswers, invokeContestMark, type AiMarkInvokeResult } from '../../utils/shadowMark.ts';
import MarkDisputePanel, { type MarkDisputeState } from './MarkDisputePanel.tsx';
import {
  formatMarkFraction,
  verdictFromMarks,
  verdictLabel,
  verdictColor,
  type PracticeMarkResult,
  type MarkVerdict,
} from '../../utils/markDisplay.ts';
import DifficultyPillSlider from './DifficultyPillSlider.tsx';
import MarksSelect from './MarksSelect.tsx';
import { DifficultyBucket, matchesDifficultyBucket } from './difficulty.ts';
import {
  MarksBucket,
  getMarksFilterRange,
  isMarksBucket,
  matchesMarksBucket,
} from './marks.ts';
import {
  choicesAsFlatStrings,
  getTableChoiceData,
  isMultipleChoiceQuestion,
} from '../../utils/questionChoices.ts';
import { McChoiceTableStudent } from '../../components/McChoiceTableStudent.tsx';

/* ───────── types ───────── */
type YearRow = { id: string; year: string | number };

type Subject = { id: string; name: string; year_id: string | null };

type EquationRow = { id: string; equation: string; category_id?: string | null; category_name?: string };

type Category = {
  id: string;
  name: string;
  parent_category_id: string | null;
  subject_id: string | null;
};

type Tag = { id: string; name: string };

/* ───────── utils ───────── */
const shuffle = <T,>(arr: T[]) =>
  arr
    .map((v) => ({ v, k: Math.random() } as const))
    .sort((a, b) => a.k - b.k)
    .map((e) => e.v);

const asChoiceArray = (choices: any): string[] => choicesAsFlatStrings(choices);
const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const formatMarkValue = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(1));
const dbTypesForFilter = (type: 'mc' | 'input') => (type === 'mc' ? ['mc', 'table'] : ['input']);

const aiResultToPracticeMark = (
  aiResult: AiMarkInvokeResult,
  fallbackMax: number,
): PracticeMarkResult => {
  const max = aiResult.maxMarks > 0 ? aiResult.maxMarks : fallbackMax;
  const feedback =
    aiResult.feedback?.trim() ||
    aiResult.partFeedback?.final?.comment?.trim() ||
    '';
  return {
    awarded: aiResult.awarded,
    max,
    feedback,
    verdict: verdictFromMarks(aiResult.awarded, max),
    partFeedback: aiResult.partFeedback,
    model: aiResult.model,
    tier: aiResult.tier,
  };
};

/* ───────── URL serialization helpers ───────── */
const serializeFiltersToURL = (
  treeIds: string[],
  types: Array<'mc' | 'input'>,
  tags: Tag[],
  difficulty: DifficultyBucket | null,
  marks: MarksBucket | null,
): URLSearchParams => {
  const params = new URLSearchParams();
  if (treeIds.length > 0) params.set('tree', treeIds.join(','));
  if (types.length === 1) params.set('types', types[0]);
  if (tags.length > 0) params.set('tags', tags.map((t) => t.id).join(','));
  if (difficulty) params.set('difficulty', difficulty);
  if (marks) params.set('marks', marks);
  return params;
};

const deserializeFiltersFromURL = (
  params: URLSearchParams,
  allTags: Tag[],
): {
  treeIds: string[];
  types: Array<'mc' | 'input'>;
  tags: Tag[];
  difficulty: DifficultyBucket | null;
  marks: MarksBucket | null;
} => {
  const treeIds = params.get('tree')?.split(',').filter(Boolean) ?? [];
  const typesParam = params.get('types');
  const tagIds = params.get('tags')?.split(',').filter(Boolean) ?? [];
  const difficultyParam = params.get('difficulty');
  const marksParam = params.get('marks');

  const types: Array<'mc' | 'input'> =
    typesParam === 'mc' || typesParam === 'input' ? [typesParam] : ['mc', 'input'];
  const tags = allTags.filter((t) => tagIds.includes(t.id));
  const difficulty =
    difficultyParam === 'easy' || difficultyParam === 'medium' || difficultyParam === 'hard'
      ? difficultyParam
      : null;
  const marks = isMarksBucket(marksParam) ? marksParam : null;

  return { treeIds, types, tags, difficulty, marks };
};

/* ───────── simple styles ───────── */
const wrapperStyle: React.CSSProperties = {
  maxWidth: 1100,
  margin: '0 auto',
  padding: '1.5rem 1rem',
};

/* ───────── paging constants ───────── */
const PAGE_SIZE = 15;
const PRELOAD_THRESHOLD = 3;
const GROUP_QUERY_CHUNK_SIZE = 120;
const NEXT_SHORTCUT_DISABLE_SELECTOR =
  'input, textarea, select, math-field, [contenteditable="true"], [role="textbox"], [data-disable-next-shortcut="true"]';

const isEditableEventTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  return target.closest(NEXT_SHORTCUT_DISABLE_SELECTOR) !== null;
};

const blurActiveElement = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

/* ───────── component ───────── */
const PracticePage: React.FC = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tutorSession, isTutorSession } = useTutorSession();
  const { studentId: effectiveStudentId } = useEffectiveStudentId();
  const hasCompany = useHasCompany();

  const [loading, setLoading] = useState(true); // initial data (years/subjects/cats/tags)

  // user + company
  const [companyId, setCompanyId] = useState<string | null>(null);

  // data
  const [years, setYears] = useState<YearRow[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [cats, setCats] = useState<Category[]>([]); // full tree: topics + any-depth children

  // 🔖 tags (global & scoped options)
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [tagOptions, setTagOptions] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  // committed selections - using a unified tree selection
  const [selTreeIds, setSelTreeIds] = useState<string[]>([]); // Contains year IDs, subject IDs, and category IDs
  const [selTypes, setSelTypes] = useState<Array<'mc' | 'input'>>(['mc', 'input']);
  const [selTags, setSelTags] = useState<Tag[]>([]);
  const [selDifficulty, setSelDifficulty] = useState<DifficultyBucket | null>(null);
  const [selMarks, setSelMarks] = useState<MarksBucket | null>(null);

  // drafts (dialog)
  const [draftTreeIds, setDraftTreeIds] = useState<string[]>([]);
  const [draftTypes, setDraftTypes] = useState<Array<'mc' | 'input'>>(['mc', 'input']);
  const [draftTags, setDraftTags] = useState<Tag[]>([]);
  const [draftDifficulty, setDraftDifficulty] = useState<DifficultyBucket | null>(null);
  const [draftMarks, setDraftMarks] = useState<MarksBucket | null>(null);

  // Tree structure maps
  const [catMap, setCatMap] = useState<Record<string, Category[]>>({});
  const [childrenById, setChildrenById] = useState<Record<string, string[]>>({});
  const [subjectMap, setSubjectMap] = useState<Record<string, Subject[]>>({}); // year_id -> subjects

  // practice session
  const [questions, setQuestions] = useState<Question[]>([]);
  const [idx, setIdx] = useState(0);
  const current = questions[idx];
  // Effects below key off the question's identity, not the object reference,
  // which changes on unrelated reloads.
  const currentId = current?.id;
  const tableMcChoices = useMemo(
    () => getTableChoiceData(current?.choices ?? null),
    [current?.choices],
  );
  const currentIsMultipleChoice = current
    ? isMultipleChoiceQuestion(current.type, current.choices)
    : false;
  // All short-answer (input) practice questions use the working-steps UI.
  const usesWorkingAnswerInput = current?.type === 'input';

  // past attempt lookup: question_id -> most recent practice attempt
  const [attemptMap, setAttemptMap] = useState<
    Record<string, { isCorrect: boolean; attemptedAt: string | null }>
  >({});

  // AI adjudication for canonical-answer input questions: when the deterministic
  // equivalence check marks an answer wrong, the shadow AI engine re-marks it and
  // its verdict is stored here so the UI can correct/explain the result.
  const [aiAdjudication, setAiAdjudication] = useState<Record<string, PracticeMarkResult>>({});
  const [markDisputes, setMarkDisputes] = useState<Record<string, MarkDisputeState>>({});
  const [disputeSubmitting, setDisputeSubmitting] = useState(false);
  const practiceAnswerIdsRef = useRef<Map<string, string>>(new Map());

  // equations cheat sheet for current question's category
  const [equations, setEquations] = useState<EquationRow[]>([]);
  const [equationsLoading, setEquationsLoading] = useState(false);
  const [showCheatSheetDialog, setShowCheatSheetDialog] = useState(false);

  // MC UI selection (no marking)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Map to store previously saved answers (question_id -> answer data) - for caching
  const savedAnswersRef = useRef<
    Map<
      string,
      {
        selectedIndex: number | null;
        yourAnswer: string | null;
        workingSteps?: string[];
        finalAnswer?: string | null;
      }
    >
  >(new Map());

  // typed answer for short-answer questions
  const [typedAnswer, setTypedAnswer] = useState('');
  const [workingSteps, setWorkingSteps] = useState<string[]>([]);
  const [workingDraft, setWorkingDraft] = useState('');
  const [finalAnswer, setFinalAnswer] = useState('');
  useEffect(() => {
    setTypedAnswer('');
    setWorkingSteps([]);
    setWorkingDraft('');
    setFinalAnswer('');
    const cached = currentId ? savedAnswersRef.current.get(currentId) : undefined;
    if (cached?.workingSteps?.length) {
      setWorkingSteps(cached.workingSteps);
    }
    if (cached?.finalAnswer) {
      setFinalAnswer(cached.finalAnswer);
      setTypedAnswer(cached.finalAnswer);
    } else if (cached?.yourAnswer) {
      setTypedAnswer(cached.yourAnswer);
      setFinalAnswer(cached.yourAnswer);
    }
  }, [idx, currentId]);

  const resolvePracticeTextAnswer = useCallback(() => {
    if (usesWorkingAnswerInput) {
      const draft = workingDraft.trim();
      if (draft) return draft;
      if (finalAnswer.trim()) return finalAnswer.trim();
      return workingSteps.at(-1)?.trim() ?? '';
    }
    return typedAnswer.trim();
  }, [usesWorkingAnswerInput, finalAnswer, workingDraft, workingSteps, typedAnswer]);

  const hasPracticeInputAnswer = useCallback(() => {
    if (usesWorkingAnswerInput) {
      return resolvePracticeTextAnswer() !== '';
    }
    return typedAnswer.trim() !== '';
  }, [usesWorkingAnswerInput, resolvePracticeTextAnswer, typedAnswer]);

  const handleCommitWorkingStep = useCallback((step: string) => {
    setWorkingSteps((prev) => {
      const next = [...prev, step];
      if (currentId) {
        const existing = savedAnswersRef.current.get(currentId);
        savedAnswersRef.current.set(currentId, {
          selectedIndex: existing?.selectedIndex ?? null,
          yourAnswer: existing?.yourAnswer ?? null,
          workingSteps: next,
          finalAnswer: existing?.finalAnswer ?? null,
        });
      }
      return next;
    });
  }, [currentId]);

  const handleFinalAnswerChange = useCallback((value: string) => {
    setFinalAnswer(value);
    setTypedAnswer(value);
    if (!currentId) return;
    const existing = savedAnswersRef.current.get(currentId);
    savedAnswersRef.current.set(currentId, {
      selectedIndex: existing?.selectedIndex ?? null,
      yourAnswer: value,
      workingSteps: existing?.workingSteps,
      finalAnswer: value,
    });
  }, [currentId]);

  const [practiceSelfMarks, setPracticeSelfMarks] = useState<Record<string, number>>({});
  const [practiceSelfMarkDrafts, setPracticeSelfMarkDrafts] = useState<Record<string, string>>({});
  const [practiceOverrideEnabled, setPracticeOverrideEnabled] = useState<Record<string, boolean>>({});

  // Reveal answer
  const [showAnswer, setShowAnswer] = useState(false);
  const [aiMarkChecking, setAiMarkChecking] = useState(false);
  useEffect(() => setShowAnswer(false), [idx]);

  // Track time spent on current question
  const questionStartTimeRef = useRef<number>(Date.now());
  useEffect(() => {
    questionStartTimeRef.current = Date.now();
  }, [idx]);

  // Track if we've restored filters from URL (to avoid restoring multiple times)
  const hasRestoredFromURL = useRef(false);
  const hasAttemptedAutoLoad = useRef(false);

  // filters modal
  const [filtersOpen, setFiltersOpen] = useState(false);
  const openFilters = () => {
    blurActiveElement();
    setDraftTreeIds(selTreeIds);
    setDraftTypes(selTypes);
    setDraftTags(selTags);
    setDraftDifficulty(selDifficulty);
    setDraftMarks(selMarks);
    setFiltersOpen(true);
  };

  /* ───────── paging state (local pool) ───────── */
  const [pool, setPool] = useState<Question[]>([]);
  const [poolCursor, setPoolCursor] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasCompletedQuestionLoad, setHasCompletedQuestionLoad] = useState(false);
  const [restrictIds, setRestrictIds] = useState<string[] | null>(null);

  // Stable "key" for the active filters to know when to reset paging
  const filtersKey = useMemo(() => {
    const key = {
      t: selTreeIds.slice().sort(),
      ty: selTypes.slice().sort(),
      g: selTags.map((t) => t.id).sort(),
      d: selDifficulty,
      m: selMarks,
    };
    return JSON.stringify(key);
  }, [selTreeIds, selTypes, selTags, selDifficulty, selMarks]);

  /* ───────── quick lookup maps & origin resolver ───────── */
  const catById = useMemo(() => {
    const m: Record<string, Category> = {};
    cats.forEach((c) => (m[c.id] = c));
    return m;
  }, [cats]);

  const subjById = useMemo(() => {
    const m: Record<string, Subject> = {};
    subjects.forEach((s) => (m[s.id] = s));
    return m;
  }, [subjects]);

  const yearById = useMemo(() => {
    const m: Record<string, YearRow> = {};
    years.forEach((y) => (m[y.id] = y));
    return m;
  }, [years]);

  // climb up parent_category_id chain until we find a category with subject_id
  const findSubjectForCategory = (startCatId: string) => {
    let walker = catById[startCatId];
    const guard = new Set<string>(); // prevent cycles (just in case)
    while (walker && !walker.subject_id && walker.parent_category_id && !guard.has(walker.id)) {
      guard.add(walker.id);
      walker = catById[walker.parent_category_id];
    }
    const subjectId = walker?.subject_id ?? null;
    return subjectId ? subjById[subjectId] : undefined;
  };

  const getQuestionOrigin = (q: Question) => {
    const startCat = catById[q.category_id];
    let leaf = startCat?.name ?? 'Unknown';
    let chain: string[] = [];

    let temp = startCat;
    let hops = 0;
    while (temp && hops < 3 && temp.parent_category_id) {
      const parent = catById[temp.parent_category_id];
      if (!parent) break;
      chain.unshift(parent.name);
      temp = parent;
      hops += 1;
    }
    const categoryText = chain.length ? `${chain.join(' › ')} › ${leaf}` : leaf;

    const subj = startCat ? findSubjectForCategory(startCat.id) : undefined;
    const subjectName = subj?.name ?? '—';
    const yr = subj?.year_id ? yearById[subj.year_id] : undefined;
    const yearText = yr ? String(yr.year) : '—';

    return { yearText, subjectName, categoryText };
  };

  /* ───────── load years, subjects, categories, tags ───────── */
  useEffect(() => {
    (async () => {
      setLoading(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      // Signed-out visitors still get the public question bank to practise
      // against. Everything that persists an answer is guarded on the student id,
      // so the logged-in features stay off until they have an account.
      let student: { id: string; company_id: string | null } | null = null;
      if (user) {
        const studentBaseQuery = supabase
          .from('students')
          .select('id, company_id');
        const { data } =
          isTutorSession && effectiveStudentId
            ? await studentBaseQuery.eq('id', effectiveStudentId).maybeSingle()
            : await studentBaseQuery.eq('user_id', user.id).maybeSingle();
        student = (data as typeof student) ?? null;
      }

      setCompanyId(student?.company_id ?? null);

      // subjects the student picked, else the whole shared bank
      let subjectIds: string[] = [];
      if (student) {
        const { data: ss } = await supabase
          .from('student_subjects')
          .select('subject_id')
          .eq('student_id', student.id);
        subjectIds = (ss ?? []).map((r) => r.subject_id);
      }

      let subjectQuery = supabase.from('subjects').select('id, name, year_id');
      if (subjectIds.length) subjectQuery = subjectQuery.in('id', subjectIds);
      const { data: subjectData } = await subjectQuery;
      const subjectsRows: Subject[] = ((subjectData ?? []) as any[]).map((r) => ({
        id: r.id,
        name: r.name,
        year_id: r.year_id ?? null,
      }));
      setSubjects(subjectsRows);

      // Build subject map by year
      const subjMap: Record<string, Subject[]> = {};
      subjectsRows.forEach((s) => {
        if (s.year_id) {
          (subjMap[s.year_id] ||= []).push(s);
        }
      });
      setSubjectMap(subjMap);

      // Load the entire category tree reachable from these subjects (any depth)
      if (subjectsRows.length) {
        const subjIds = subjectsRows.map((s) => s.id);

        // 1) fetch root topics (parent null, subject set)
        const { data: rootTopics, error: rootErr } = await supabase
          .from('categories')
          .select('id, name, parent_category_id, subject_id')
          .in('subject_id', subjIds)
          .is('parent_category_id', null);

        if (rootErr) {
          console.error(rootErr.message);
          setCats([]);
          setCatMap({});
          setChildrenById({});
        } else {
          const all: Record<string, Category> = {};
          const queue: string[] = [];

          (rootTopics ?? []).forEach((c: any) => {
            all[c.id] = c as Category;
            queue.push(c.id);
          });

          // 2) BFS down: fetch children repeatedly until no more
          while (queue.length) {
            const batchParentIds = Array.from(new Set(queue.splice(0, 50)));
            const { data: kids, error: kidsErr } = await supabase
              .from('categories')
              .select('id, name, parent_category_id, subject_id')
              .in('parent_category_id', batchParentIds);

            if (kidsErr) {
              console.error(kidsErr.message);
              break;
            }
            const newChildren: string[] = [];
            (kids ?? []).forEach((c: any) => {
              if (!all[c.id]) {
                all[c.id] = c as Category;
                if (c.parent_category_id) newChildren.push(c.id);
              }
            });
            if (newChildren.length) queue.push(...newChildren);
          }

          const allCats = Object.values(all);
          setCats(allCats);

          // Build maps for tree structure
          const map: Record<string, Category[]> = {};
          const childMap: Record<string, string[]> = {};
          
          allCats.forEach((c) => {
            const p = c.parent_category_id ?? 'root';
            (map[p] ||= []).push(c);
          });
          
          allCats.forEach((c) => {
            if (c.parent_category_id) {
              (childMap[c.parent_category_id] ||= []).push(c.id);
            }
          });

          setCatMap(map);
          setChildrenById(childMap);
        }
      } else {
        setCats([]);
        setCatMap({});
        setChildrenById({});
      }

      // Years are a shared global bank, not company-scoped
      const { data: yrs } = await supabase
        .from('years')
        .select('id, year')
        .order('year', { ascending: true });
      setYears((yrs ?? []) as YearRow[]);

      // Load *all* tags once; we'll scope them later based on selections
      const { data: tagsRows } = await supabase.from('tags').select('id, name').order('name');
      setAllTags((tagsRows ?? []) as Tag[]);
      setTagOptions((tagsRows ?? []) as Tag[]); // initial default

      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, effectiveStudentId, isTutorSession]);

  /* ───────── restore filters from URL on initial load ───────── */
  useEffect(() => {
    if (
      !hasRestoredFromURL.current &&
      !loading &&
      years.length > 0 &&
      subjects.length > 0 &&
      cats.length > 0 &&
      allTags.length > 0
    ) {
      const restored = deserializeFiltersFromURL(searchParams, allTags);
      
      // Only restore if URL has params (to avoid overwriting with defaults on first visit)
      const hasParams = searchParams.toString().length > 0;
      if (hasParams) {
        setSelTreeIds(restored.treeIds);
        setSelTypes(restored.types);
        setSelTags(restored.tags);
        setSelDifficulty(restored.difficulty);
        setSelMarks(restored.marks);
        
        // Load questions with restored filters
        (async () => {
          setLoading(true);
          try {
            await loadQuestionsFrom(
              restored.treeIds,
              restored.types,
              restored.tags,
              restored.difficulty,
              restored.marks,
              true,
            );
          } finally {
            setLoading(false);
          }
        })();
      }
      hasRestoredFromURL.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, years.length, subjects.length, cats.length, allTags.length]);

  /* ───────── Build unified tree: Years → Subjects → Categories ───────── */
  const buildCategoryChildren = useCallback((parentId: string): any[] => {
    return (catMap[parentId] || []).map((cat) => ({
      id: `cat_${cat.id}`,
      label: cat.name,
      children: buildCategoryChildren(cat.id),
    }));
  }, [catMap]);

  const buildTreeItems = useCallback((): any[] => {
    return years.map((year) => {
      const yearSubjects = subjectMap[year.id] || [];
      return {
        id: `year_${year.id}`,
        label: String(year.year),
        children: yearSubjects.map((subject) => {
          // Get root categories for this subject
          const rootCats = (catMap['root'] || []).filter((c) => c.subject_id === subject.id);
          return {
            id: `subject_${subject.id}`,
            label: subject.name,
            children: rootCats.map((cat) => ({
              id: `cat_${cat.id}`,
              label: cat.name,
              children: buildCategoryChildren(cat.id),
            })),
          };
        }),
      };
    });
  }, [years, subjectMap, catMap, buildCategoryChildren]);

  // Get all descendants in the unified tree
  const getTreeDescendants = useCallback((id: string): string[] => {
    const out: string[] = [];
    
    // Parse the ID to determine type
    if (id.startsWith('year_')) {
      const yearId = id.replace('year_', '');
      // Get all subjects for this year
      const yearSubjects = subjectMap[yearId] || [];
      for (const subj of yearSubjects) {
        const subjId = `subject_${subj.id}`;
        out.push(subjId);
        // Get all categories for this subject
        const rootCats = (catMap['root'] || []).filter((c) => c.subject_id === subj.id);
        for (const cat of rootCats) {
          const catId = `cat_${cat.id}`;
          out.push(catId);
          // Get all descendants of this category
          const stack = [...(childrenById[cat.id] || [])];
          while (stack.length) {
            const childId = stack.pop()!;
            const childCatId = `cat_${childId}`;
            out.push(childCatId);
            stack.push(...(childrenById[childId] || []));
          }
        }
      }
    } else if (id.startsWith('subject_')) {
      const subjectId = id.replace('subject_', '');
      // Get all categories for this subject
      const rootCats = (catMap['root'] || []).filter((c) => c.subject_id === subjectId);
      for (const cat of rootCats) {
        const catId = `cat_${cat.id}`;
        out.push(catId);
        // Get all descendants of this category
        const stack = [...(childrenById[cat.id] || [])];
        while (stack.length) {
          const childId = stack.pop()!;
          const childCatId = `cat_${childId}`;
          out.push(childCatId);
          stack.push(...(childrenById[childId] || []));
        }
      }
    } else if (id.startsWith('cat_')) {
      const catId = id.replace('cat_', '');
      // Get all descendants of this category
      const stack = [...(childrenById[catId] || [])];
      while (stack.length) {
        const childId = stack.pop()!;
        const childCatId = `cat_${childId}`;
        out.push(childCatId);
        stack.push(...(childrenById[childId] || []));
      }
    }
    
    return out;
  }, [subjectMap, catMap, childrenById]);

  const getTreeAncestors = useCallback((id: string): string[] => {
    const ancestors: string[] = [];

    if (id.startsWith('cat_')) {
      let cat = catById[id.replace('cat_', '')];
      const guard = new Set<string>();
      while (cat && !guard.has(cat.id)) {
        guard.add(cat.id);
        if (cat.parent_category_id) {
          ancestors.push(`cat_${cat.parent_category_id}`);
          cat = catById[cat.parent_category_id];
          continue;
        }
        if (cat.subject_id) {
          ancestors.push(`subject_${cat.subject_id}`);
          const subject = subjById[cat.subject_id];
          if (subject?.year_id) ancestors.push(`year_${subject.year_id}`);
        }
        break;
      }
      return ancestors;
    }

    if (id.startsWith('subject_')) {
      const subject = subjById[id.replace('subject_', '')];
      return subject?.year_id ? [`year_${subject.year_id}`] : [];
    }

    return ancestors;
  }, [catById, subjById]);

  // Keep ancestors visibly selected whenever any descendant is selected.
  const enforceTreeParentSelection = useCallback((sel: Set<string>): Set<string> => {
    let changed = true;
    while (changed) {
      changed = false;

      for (const id of Array.from(sel)) {
        for (const ancestor of getTreeAncestors(id)) {
          if (!sel.has(ancestor)) {
            sel.add(ancestor);
            changed = true;
          }
        }
      }

      for (const cat of cats) {
        const catId = `cat_${cat.id}`;
        if (!sel.has(catId) || !(childrenById[cat.id] || []).length) continue;
        if (!getTreeDescendants(catId).some((descendant) => sel.has(descendant))) {
          sel.delete(catId);
          changed = true;
        }
      }

      for (const subject of subjects) {
        const subjectId = `subject_${subject.id}`;
        if (!sel.has(subjectId)) continue;
        if (!getTreeDescendants(subjectId).some((descendant) => sel.has(descendant))) {
          sel.delete(subjectId);
          changed = true;
        }
      }

      for (const year of years) {
        const yearId = `year_${year.id}`;
        if (!sel.has(yearId)) continue;
        if (!getTreeDescendants(yearId).some((descendant) => sel.has(descendant))) {
          sel.delete(yearId);
          changed = true;
        }
      }
    }
    return sel;
  }, [years, subjects, cats, childrenById, getTreeAncestors, getTreeDescendants]);

  // Handle tree selection change
  const handleTreeSelectionChange = useCallback((_: unknown, ids: string[]) => {
    const prev = new Set(draftTreeIds);
    let next = new Set(ids);

    const added = ids.filter((id) => !prev.has(id));
    const removed = draftTreeIds.filter((id) => !next.has(id));

    // If a parent was added, add all descendants
    for (const a of added) {
      for (const d of getTreeDescendants(a)) {
        next.add(d);
      }
    }
    
    // If a parent was removed, remove all descendants
    for (const r of removed) {
      for (const d of getTreeDescendants(r)) {
        next.delete(d);
      }
    }

    // Ensure parents reflect children (auto select/unselect)
    next = enforceTreeParentSelection(next);

    setDraftTreeIds(Array.from(next));
  }, [draftTreeIds, getTreeDescendants, enforceTreeParentSelection]);

  /* ───────── helpers to compute category ids from tree selection ───────── */
  const buildCategoryIds = useCallback((treeIds: string[]) => {
    // If nothing is selected, return all categories (show random questions)
    if (treeIds.length === 0) {
      return cats.map((c) => c.id);
    }
    
    const result = new Set<string>();
    
    // Extract year IDs, subject IDs, and category IDs from tree selection
    const yearIds = new Set<string>();
    const subjectIds = new Set<string>();
    const categoryIds = new Set<string>();
    
    for (const id of treeIds) {
      if (id.startsWith('year_')) {
        yearIds.add(id.replace('year_', ''));
      } else if (id.startsWith('subject_')) {
        subjectIds.add(id.replace('subject_', ''));
      } else if (id.startsWith('cat_')) {
        categoryIds.add(id.replace('cat_', ''));
      }
    }
    
    // If specific categories are selected, use those (they already include descendants from tree selection)
    if (categoryIds.size > 0) {
      // The tree selection already includes all descendants when a parent is selected
      // So we can just use the category IDs directly
      categoryIds.forEach((cid) => result.add(cid));
      return Array.from(result);
    }
    
    // If subjects are selected (but no specific categories), get all categories for those subjects
    if (subjectIds.size > 0) {
      const byParent: Record<string, string[]> = {};
      cats.forEach((c) => {
        if (c.parent_category_id) {
          (byParent[c.parent_category_id] ||= []).push(c.id);
        }
      });
      
      for (const s of subjectIds) {
        const rootCats = (catMap['root'] || []).filter((c) => c.subject_id === s);
        const queue = [...rootCats.map((c) => c.id)];
        rootCats.forEach((c) => result.add(c.id));
        
        while (queue.length) {
          const p = queue.shift()!;
          const kids = byParent[p] || [];
          for (const k of kids) {
            if (!result.has(k)) {
              result.add(k);
              queue.push(k);
            }
          }
        }
      }
      
      return Array.from(result);
    }
    
    // If years are selected (but no subjects or categories), get all categories for subjects in those years
    if (yearIds.size > 0) {
      const effectiveSubjects = subjects.filter((s) => s.year_id && yearIds.has(s.year_id));
      const subjSet = new Set(effectiveSubjects.map((s) => s.id));
      const byParent: Record<string, string[]> = {};
      
      cats.forEach((c) => {
        if (c.parent_category_id) {
          (byParent[c.parent_category_id] ||= []).push(c.id);
        }
      });
      
      for (const s of subjSet) {
        const rootCats = (catMap['root'] || []).filter((c) => c.subject_id === s);
        const queue = [...rootCats.map((c) => c.id)];
        rootCats.forEach((c) => result.add(c.id));
        
        while (queue.length) {
          const p = queue.shift()!;
          const kids = byParent[p] || [];
          for (const k of kids) {
            if (!result.has(k)) {
              result.add(k);
              queue.push(k);
            }
          }
        }
      }
      
      return Array.from(result);
    }

    // Fallback: return empty if nothing matches
    return [];
  }, [cats, subjects, catMap]);

  /* ───────── Scope tag options (draft) to selection ───────── */
  useEffect(() => {
    (async () => {
      const categoryIds = buildCategoryIds(draftTreeIds);
      if (!categoryIds.length) {
        setTagOptions(allTags);
        return;
      }

      setTagsLoading(true);
      try {
        let qt = supabase
          .from('question_tags')
          .select('tag_id, questions!inner(category_id, type, group_id, difficulty, marks)')
          .in('questions.category_id', categoryIds);

        if (draftTypes.length === 1) {
          qt = qt.in('questions.type', dbTypesForFilter(draftTypes[0]));
        }
        if (draftMarks) {
          const range = getMarksFilterRange(draftMarks);
          qt = qt.gte('questions.marks', range.min);
          if (range.max !== null) qt = qt.lte('questions.marks', range.max);
        }

        const { data: qtRows, error: qtErr } = await qt;
        if (qtErr) {
          console.error(qtErr.message);
          setTagOptions(allTags);
          return;
        }

        const scopedQuestionTagRows = (qtRows ?? []).filter(
          (r: any) =>
            matchesDifficultyBucket(r.questions?.difficulty, draftDifficulty) &&
            matchesMarksBucket(r.questions?.marks, draftMarks),
        );

        const tagIds = Array.from(
          new Set(scopedQuestionTagRows.map((r: any) => r.tag_id)),
        ).filter(Boolean);
        if (!tagIds.length) {
          setTagOptions([]);
          setDraftTags([]);
          return;
        }

        const { data: scopedTags, error: tagsErr } = await supabase
          .from('tags')
          .select('id, name')
          .in('id', tagIds)
          .order('name');

        if (tagsErr) {
          console.error(tagsErr.message);
          setTagOptions(allTags);
          return;
        }

        const scoped = (scopedTags ?? []) as Tag[];
        setTagOptions(scoped);
        setDraftTags((prev) => prev.filter((t) => scoped.some((s) => s.id === t.id)));
      } finally {
        setTagsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftTreeIds, draftTypes, draftDifficulty, draftMarks, buildCategoryIds, allTags]);

  /* ───────── unit-aware loader (groups kept together) ───────── */
  const loadQuestionsFrom = async (
    treeIdsSel: string[],
    typesSel: Array<'mc' | 'input'>,
    tagsSel: Tag[],
    difficultySel: DifficultyBucket | null,
    marksSel: MarksBucket | null,
    reset: boolean = true,
  ) => {
    if (reset) setHasCompletedQuestionLoad(false);
    const categoryIds = buildCategoryIds(treeIdsSel);

    if (!categoryIds.length) {
      setQuestions([]);
      setPool([]);
      setPoolCursor(0);
      setIdx(0);
      setHasMore(false);
      setRestrictIds(null);
      if (reset) setHasCompletedQuestionLoad(true);
      return;
    }

    // Resolve tag restriction once on reset
    let nextRestrict: string[] | null = restrictIds;
    if (reset) {
      if (tagsSel.length) {
        const tagIds = tagsSel.map((t) => t.id);
        const { data: qtags, error: tagErr } = await supabase
          .from('question_tags')
          .select('question_id')
          .in('tag_id', tagIds);

        if (tagErr) {
          console.error(tagErr.message);
          setQuestions([]);
          setPool([]);
          setPoolCursor(0);
          setIdx(0);
          setHasMore(false);
          setRestrictIds(null);
          return;
        }

        const ids = Array.from(
          new Set((qtags ?? []).map((r: any) => r.question_id as string)),
        );
        if (ids.length === 0) {
          setQuestions([]);
          setPool([]);
          setPoolCursor(0);
          setIdx(0);
          setHasMore(false);
          setRestrictIds([]);
          if (reset) setHasCompletedQuestionLoad(true);
          return;
        }
        nextRestrict = ids;
        setRestrictIds(ids);
      } else {
        nextRestrict = null;
        setRestrictIds(null);
      }
    }

    const flagged = await fetchOpenFlaggedIds();

    // ---- 1) Standalone questions (no group) ----
    const restrictSet = nextRestrict ? new Set(nextRestrict) : null;

    const { data: soloRows, error: soloErr } = await queryQuestionsWithMarkingFallback(
      (select) => {
        let soloQ = supabase
          .from('questions')
          .select(select)
          .in('category_id', categoryIds)
          .is('group_id', null);
        if (typesSel.length === 1) soloQ = soloQ.in('type', dbTypesForFilter(typesSel[0]));
        if (nextRestrict) soloQ = soloQ.in('id', nextRestrict);
        if (marksSel) {
          const range = getMarksFilterRange(marksSel);
          soloQ = soloQ.gte('marks', range.min);
          if (range.max !== null) soloQ = soloQ.lte('marks', range.max);
        }
        return soloQ;
      },
      PRACTICE_SOLO_SELECT,
    );
    if (soloErr) {
      console.error(soloErr.message);
      if (reset) {
        setQuestions([]);
        setPool([]);
        setPoolCursor(0);
        setIdx(0);
        setHasMore(false);
        setHasCompletedQuestionLoad(true);
      }
      return;
    }
    let baseSoloRows = (soloRows ?? []) as Question[];
    if (restrictSet) baseSoloRows = baseSoloRows.filter((q) => restrictSet.has(q.id));
    if (flagged.questionIds.size) {
      baseSoloRows = baseSoloRows.filter((q) => !flagged.questionIds.has(q.id));
    }
    const filteredSoloRows = baseSoloRows.filter(
      (q) =>
        matchesDifficultyBucket(q.difficulty, difficultySel) &&
        matchesMarksBucket(q.marks, marksSel),
    );

    const standaloneUnits: Question[][] = filteredSoloRows.map((q) => [q]);

    // ---- 2) Grouped units: fetch groups by category, then their parts ----
    const { data: groups, error: gErr } = await supabase
      .from('question_groups')
      .select('id, prompt')
      .in('category_id', categoryIds);
    if (gErr) {
      console.error(gErr.message);
    }

    let groupUnits: Question[][] = [];
    if (groups && groups.length) {
      const groupIds = groups.map((g: any) => g.id);
      const byGroup: Record<string, Question[]> = {};
      for (let i = 0; i < groupIds.length; i += GROUP_QUERY_CHUNK_SIZE) {
        const chunk = groupIds.slice(i, i + GROUP_QUERY_CHUNK_SIZE);
        const { data: partRows, error: pErr } = await queryQuestionsWithMarkingFallback(
          (select) => {
            let partsQ = supabase
              .from('questions')
              .select(select)
              .in('group_id', chunk);
            if (typesSel.length === 1) partsQ = partsQ.in('type', dbTypesForFilter(typesSel[0]));
            if (nextRestrict) partsQ = partsQ.in('id', nextRestrict);
            if (marksSel) {
              const range = getMarksFilterRange(marksSel);
              partsQ = partsQ.gte('marks', range.min);
              if (range.max !== null) partsQ = partsQ.lte('marks', range.max);
            }
            return partsQ;
          },
          PRACTICE_PART_SELECT,
        );
        if (pErr) {
          console.error(pErr.message);
          continue;
        }
        let basePartRows = partRows ?? [];
        if (restrictSet) basePartRows = basePartRows.filter((p: any) => restrictSet.has(p.id));
        const filteredPartRows = basePartRows.filter(
          (p: any) =>
            matchesDifficultyBucket(p.difficulty, difficultySel) &&
            matchesMarksBucket(p.marks, marksSel),
        );

        filteredPartRows.forEach((p: any) => {
          const gid = p.group_id as string;
          if (!byGroup[gid]) byGroup[gid] = [];
          byGroup[gid].push(p as Question);
        });
      }

      groupUnits = Object.entries(byGroup)
        .filter(([gid]) => !flagged.groupIds.has(gid))
        .map(([gid, arr]) => {
          const shared = groups.find((g: any) => g.id === gid)?.prompt ?? null;
          const sorted = arr.sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));
          if (!sorted.length) return [];
          return sorted.map((p, idx) => ({
            ...p,
            group_prompt: shared,
            part_label: `Part ${String.fromCharCode(65 + idx)}`,
          }));
        })
        .filter((unit) => unit.length > 0);
    }

    // ---- 3) Shuffle by unit, then flatten ----
    const units = [...standaloneUnits, ...groupUnits];
    const flattened = shuffle(units).flat();

    if (reset) {
      setPool(flattened);
      setPoolCursor(Math.min(PAGE_SIZE, flattened.length));
      setQuestions(flattened.slice(0, PAGE_SIZE));
      setIdx(0);
      setHasMore(flattened.length > PAGE_SIZE);
      setHasCompletedQuestionLoad(true);
    } else {
      const nextSlice = flattened.slice(poolCursor, poolCursor + PAGE_SIZE);
      setQuestions((prev) => [...prev, ...nextSlice]);
      const nextCursor = poolCursor + nextSlice.length;
      setPool(flattened);
      setPoolCursor(nextCursor);
      setHasMore(nextCursor < flattened.length);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextSlice = pool.slice(poolCursor, poolCursor + PAGE_SIZE);
      setQuestions((prev) => [...prev, ...nextSlice]);
      const nextCursor = poolCursor + nextSlice.length;
      setPoolCursor(nextCursor);
      setHasMore(nextCursor < pool.length);
    } finally {
      setLoadingMore(false);
    }
  };

  /* ───────── auto-load randomized questions on first ready ───────── */
  useEffect(() => {
    if (hasAttemptedAutoLoad.current) return;
    const hasParams = searchParams.toString().length > 0;
    if (!loading && questions.length === 0 && (!hasParams || hasRestoredFromURL.current)) {
      hasAttemptedAutoLoad.current = true;
      (async () => {
        setLoading(true);
        try {
          await loadQuestionsFrom(
            selTreeIds,
            selTypes,
            selTags,
            selDifficulty,
            selMarks,
            true,
          );
        } finally {
          setLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  /* ───────── prefetch when near the end ───────── */
  useEffect(() => {
    if (questions.length && hasMore && !loadingMore && idx >= questions.length - PRELOAD_THRESHOLD) {
      loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, questions.length, hasMore, loadingMore, filtersKey]);

  /* ───────── reset selection when moving to a new question ───────── */
  useEffect(() => {
    if (currentIsMultipleChoice) {
      const cached = current ? savedAnswersRef.current.get(current.id) : undefined;
      setSelectedIndex(cached?.selectedIndex ?? null);
    } else {
      setSelectedIndex(null);
    }
  }, [idx, current, currentIsMultipleChoice]);

  /* ───────── fetch past attempt data for loaded questions ───────── */
  useEffect(() => {
    if (!effectiveStudentId || questions.length === 0) return;
    const BATCH = 150;
    let cancelled = false;
    (async () => {
      const ids = questions.map((q) => q.id);
      const newOnly = ids.filter((id) => !(id in attemptMap));
      if (newOnly.length === 0) return;

      const found: Record<string, { isCorrect: boolean; attemptedAt: string | null }> = {};
      for (let i = 0; i < newOnly.length; i += BATCH) {
        const chunk = newOnly.slice(i, i + BATCH);
        const { data } = await markingDb
          .from('practice_answers')
          .select('question_id, is_correct, created_at')
          .eq('student_user_id', effectiveStudentId)
          .in('question_id', chunk);
        if (cancelled) return;
        (data ?? []).forEach((r: any) => {
          found[r.question_id] = {
            isCorrect: r.is_correct === true,
            attemptedAt: r.created_at ?? null,
          };
        });
      }
      if (!cancelled) setAttemptMap((prev) => ({ ...prev, ...found }));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveStudentId, questions]);

  /* ───────── save practice answer ───────── */
  const savePracticeAnswer = useCallback(
    async (
      question: Question,
      mcIndex: number | null,
      textAnswer: string,
      source: 'debounce' | 'next' | 'prev' | 'show_answer' | 'manual' = 'manual',
      workingStepsForSave: string[] = [],
    ) => {
      if (!question) return;
      if (!isTutorSession && !effectiveStudentId) return;

      const timeSpent = Math.floor((Date.now() - questionStartTimeRef.current) / 1000);

      // Compute is_correct
      const isMultipleChoice = isMultipleChoiceQuestion(question.type, question.choices);
      // MC attempts are graded and persisted only when the student reveals the answer.
      if (isMultipleChoice && source !== 'show_answer') {
        return;
      }
      const trimmedInput = textAnswer.trim();
      const maxMarks = Number(question.marks ?? 0);
      const answered =
        isMultipleChoice
          ? mcIndex !== null
          : trimmedInput.length > 0;
      const submittable = toSubmittableQuestion(question);
      const markingType = resolveMarkingType(submittable);
      const aiMarkEligible = isAiMarkingType(markingType);
      const markOutcome =
        answered && clientScoresMarkingType(markingType)
          ? scoreQuestionSubmission(submittable, {
              finalAnswer: isMultipleChoice ? null : trimmedInput,
              selectedIndex: isMultipleChoice ? mcIndex : null,
            })
          : null;
      const isCorrect = markOutcome != null && markOutcome.totalAwarded >= maxMarks && maxMarks > 0;
      const hasDeterministicAutoMark = markOutcome != null && !markOutcome.requiresReview;
      const hasAutoMark = hasDeterministicAutoMark || (aiMarkEligible && answered);
      const overrideMarkRaw = question.type === 'input' ? practiceSelfMarks[question.id] : undefined;
      const overrideMark =
        Number.isFinite(overrideMarkRaw) ? clamp(Number(overrideMarkRaw), 0, maxMarks) : null;
      const earnedMarks = overrideMark ?? (hasDeterministicAutoMark ? markOutcome?.totalAwarded ?? 0 : aiMarkEligible ? 0 : null);

      try {
        if (isTutorSession) {
          // Session counting is mark-based: only marked attempts count.
          await logTutorSessionEvent(tutorSession?.id, 'question_attempt', {
            mode: 'practice',
            attempt_source: source,
            answered: earnedMarks != null || aiMarkEligible,
            question_id: question.id,
            correct: earnedMarks != null ? earnedMarks === maxMarks : null,
            earned_marks: earnedMarks,
            max_marks: maxMarks,
            time_spent_seconds: timeSpent,
            selected_index: isMultipleChoice ? mcIndex : null,
            your_answer: question.type === 'input' ? (trimmedInput || null) : null,
          });
          if (aiMarkEligible && trimmedInput) {
            if (source === 'show_answer') {
              const aiResult = await invokeShadowMarkAiSubmission(
                {
                  questionId: question.id,
                  studentAnswer: trimmedInput,
                  workingSteps: workingStepsForSave,
                },
                { awaitResult: true },
              );
              if (aiResult) {
                const mark = aiResultToPracticeMark(aiResult, maxMarks);
                setAiAdjudication((prev) => ({
                  ...prev,
                  [question.id]: mark,
                }));
                setAttemptMap((prev) => ({
                  ...prev,
                  [question.id]: {
                    isCorrect: mark.verdict === 'correct',
                    attemptedAt: new Date().toISOString(),
                  },
                }));
              }
            } else {
              invokeShadowMarkAiSubmission({
                questionId: question.id,
                studentAnswer: trimmedInput,
                workingSteps: workingStepsForSave,
              });
            }
          }
          return;
        }

        if (!hasAutoMark || !effectiveStudentId) return;

        const answerData: {
          student_user_id: string;
          question_id: string;
          selected_index?: number | null;
          your_answer?: string | null;
          is_correct: boolean;
          time_spent_seconds: number;
        } = {
          student_user_id: effectiveStudentId,
          question_id: question.id,
          is_correct: isCorrect,
          time_spent_seconds: timeSpent,
        };

        if (isMultipleChoice) {
          answerData.selected_index = mcIndex;
          answerData.your_answer = null;
        } else {
          answerData.selected_index = null;
          answerData.your_answer = trimmedInput || null;
        }

        // Upsert: update if exists, insert if not
        const { data: savedRow, error } = await markingDb
          .from('practice_answers')
          .upsert(answerData, {
            onConflict: 'student_user_id,question_id',
          })
          .select('id')
          .single();

        if (error) {
          console.error('Error saving practice answer:', error);
        } else {
          if (question.type === 'input' && savedRow?.id) {
            if (aiMarkEligible) {
              if (source === 'show_answer') {
                const aiResult = await invokeShadowMarkAiSubmission(
                  {
                    questionId: question.id,
                    studentAnswer: trimmedInput,
                    practiceAnswerId: String(savedRow.id),
                    workingSteps: workingStepsForSave,
                  },
                  { awaitResult: true },
                );
                if (aiResult) {
                  const mark = aiResultToPracticeMark(aiResult, maxMarks);
                  setAiAdjudication((prev) => ({
                    ...prev,
                    [question.id]: mark,
                  }));
                  setAttemptMap((prev) => ({
                    ...prev,
                    [question.id]: {
                      isCorrect: mark.verdict === 'correct',
                      attemptedAt: new Date().toISOString(),
                    },
                  }));
                }
              } else {
                invokeShadowMarkAiSubmission({
                  questionId: question.id,
                  studentAnswer: trimmedInput,
                  practiceAnswerId: String(savedRow.id),
                  workingSteps: workingStepsForSave,
                });
              }
            } else {
              invokeShadowMarkPracticeAnswers([String(savedRow.id)]);
            }
          }
          savedAnswersRef.current.set(question.id, {
            selectedIndex: mcIndex,
            yourAnswer: trimmedInput || null,
            ...(workingStepsForSave.length ? { workingSteps: workingStepsForSave } : {}),
            ...(trimmedInput ? { finalAnswer: trimmedInput } : {}),
          });
          if (savedRow?.id != null) {
            practiceAnswerIdsRef.current.set(question.id, String(savedRow.id));
          }
          if (answered && !aiMarkEligible) {
            setAttemptMap((prev) => ({
              ...prev,
              [question.id]: { isCorrect, attemptedAt: new Date().toISOString() },
            }));
          } else if (answered && aiMarkEligible && source !== 'show_answer') {
            // Background AI mark; result will appear on next visit or when Show Answer is used.
            setAttemptMap((prev) => ({
              ...prev,
              [question.id]: { isCorrect: false, attemptedAt: new Date().toISOString() },
            }));
          }
          return savedRow?.id != null ? String(savedRow.id) : undefined;
        }
      } catch (err) {
        console.error('Error saving practice answer:', err);
      }
      return undefined;
    },
    [effectiveStudentId, isTutorSession, practiceSelfMarks, tutorSession?.id],
  );

  /* ───────── load saved answer for current question ───────── */
  useEffect(() => {
    if (!current || !effectiveStudentId || isTutorSession || currentIsMultipleChoice) return;

    (async () => {
      try {
        const { data, error } = await markingDb
          .from('practice_answers')
          .select('selected_index, your_answer')
          .eq('student_user_id', effectiveStudentId)
          .eq('question_id', current.id)
          .maybeSingle();

        if (error) {
          console.error('Error loading saved answer:', error);
          return;
        }

        if (data) {
          if (current.type === 'input' && data.your_answer) {
            setTypedAnswer(data.your_answer);
            savedAnswersRef.current.set(current.id, {
              selectedIndex: data.selected_index,
              yourAnswer: data.your_answer,
            });
          }
          // MC selection is not restored from DB — only kept in-session after Show Answer.
        }
      } catch (err) {
        console.error('Error loading saved answer:', err);
      }
    })();
  }, [current, currentIsMultipleChoice, effectiveStudentId, isTutorSession]);

  /* ───────── nav ───────── */
  const prev = useCallback(async () => {
    const hasAssignedTutorInputMark =
      isTutorSession &&
      current?.type === 'input' &&
      Number.isFinite(practiceSelfMarks[current.id]);
    if (current && (isTutorSession || effectiveStudentId) && !hasAssignedTutorInputMark) {
      await savePracticeAnswer(
        current,
        selectedIndex,
        resolvePracticeTextAnswer(),
        'prev',
        workingSteps,
      );
    }
    setIdx((i) => Math.max(0, i - 1));
  }, [
    current,
    effectiveStudentId,
    isTutorSession,
    practiceSelfMarks,
    savePracticeAnswer,
    selectedIndex,
    resolvePracticeTextAnswer,
    workingSteps,
  ]);

  const currentMarkingType = current ? resolveMarkingType(current) : null;
  const hasCanonicalInputAnswer =
    current?.type === 'input' &&
    currentMarkingType != null &&
    isDeterministicMarkingType(currentMarkingType) &&
    typeof current.answer === 'string' &&
    current.answer.trim() !== '';
  const hasManualInputMarking = current?.type === 'input' && currentMarkingType === 'manual';
  const hasAiMarkInput = current?.type === 'input' && currentMarkingType != null && isAiMarkingType(currentMarkingType);
  const hasTutorOverrideInputMarking =
    isTutorSession && current?.type === 'input' && hasCanonicalInputAnswer;
  const canTutorAssignMark = hasManualInputMarking || hasTutorOverrideInputMarking;
  const hasShortAnswerField = current?.type === 'input';
  const isNextDisabled =
    !current || (idx === questions.length - 1 && !loadingMore && !hasMore);
  const canRevealAnswerWithEnter =
    !!current &&
    !showAnswer &&
    (currentIsMultipleChoice ? selectedIndex !== null : hasPracticeInputAnswer());

  const revealAnswer = useCallback(async () => {
    if (!current || showAnswer) return;
    let trimmed = resolvePracticeTextAnswer();
    // Treat in-progress editor text or last working step as the final answer when revealing.
    if (usesWorkingAnswerInput && trimmed && !finalAnswer.trim()) {
      setFinalAnswer(trimmed);
      setTypedAnswer(trimmed);
      if (workingDraft.trim()) setWorkingDraft('');
    }
    // AI-marked questions are scored inside savePracticeAnswer.
    const needsAiOnlyCheck = hasAiMarkInput && trimmed !== '';
    // Deterministic questions: only ask the AI to adjudicate when the fast
    // client-side equivalence check thinks the answer is wrong (cost control +
    // catches equivalent-but-rejected answers like "dy/dx = ..." vs "...").
    const canonicalNeedsAi =
      hasCanonicalInputAnswer &&
      trimmed !== '' &&
      !areMathAnswersEquivalent(trimmed, (current.answer ?? '').trim());
    const needsAiCheck = needsAiOnlyCheck || canonicalNeedsAi;
    if (needsAiCheck) setAiMarkChecking(true);
    try {
      let savedId: string | undefined;
      if (isTutorSession || effectiveStudentId) {
        savedId = await savePracticeAnswer(
          current,
          selectedIndex,
          trimmed,
          'show_answer',
          workingSteps,
        );
        if (savedId) {
          practiceAnswerIdsRef.current.set(current.id, savedId);
        }
      }
      if (canonicalNeedsAi) {
        const ai = await invokeShadowMarkAiSubmission(
          {
            questionId: current.id,
            studentAnswer: trimmed,
            practiceAnswerId: savedId,
            workingSteps,
          },
          { awaitResult: true },
        );
        if (ai) {
          const mark = aiResultToPracticeMark(ai, Number(current.marks ?? 0));
          setAiAdjudication((prev) => ({
            ...prev,
            [current.id]: mark,
          }));
          setAttemptMap((prev) => ({
            ...prev,
            [current.id]: {
              isCorrect: mark.verdict === 'correct',
              attemptedAt: new Date().toISOString(),
            },
          }));
        }
      }
    } finally {
      if (needsAiCheck) setAiMarkChecking(false);
    }
    setShowAnswer(true);
  }, [
    current,
    showAnswer,
    hasAiMarkInput,
    hasCanonicalInputAnswer,
    isTutorSession,
    effectiveStudentId,
    savePracticeAnswer,
    selectedIndex,
    resolvePracticeTextAnswer,
    workingSteps,
    usesWorkingAnswerInput,
    finalAnswer,
    workingDraft,
  ]);

  const toggleShowAnswer = useCallback(async () => {
    if (showAnswer) {
      setShowAnswer(false);
      return;
    }
    await revealAnswer();
  }, [showAnswer, revealAnswer]);

  const commitPracticeSelfMark = useCallback(async (markValue: number) => {
    if (!current || !canTutorAssignMark) return;
    const maxMarks = Number(current.marks ?? 0);
    if (!Number.isFinite(markValue) || markValue < 0 || markValue > maxMarks) {
      return;
    }

    setPracticeSelfMarks((prev) => ({
      ...prev,
      [current.id]: markValue,
    }));

    if (isTutorSession) {
      const timeSpent = Math.floor((Date.now() - questionStartTimeRef.current) / 1000);
      await logTutorSessionEvent(tutorSession?.id, 'question_attempt', {
        mode: 'practice',
        attempt_source: 'self_mark',
        answered: true,
        question_id: current.id,
        correct: null,
        earned_marks: markValue,
        max_marks: maxMarks,
        time_spent_seconds: timeSpent,
        selected_index: null,
        your_answer: typedAnswer.trim() || null,
      });
    }
  }, [canTutorAssignMark, current, isTutorSession, tutorSession?.id, typedAnswer]);

  const assignPracticeSelfMark = useCallback(async () => {
    if (!current || !canTutorAssignMark) return;
    if (hasTutorOverrideInputMarking && !practiceOverrideEnabled[current.id]) return;
    const raw = (practiceSelfMarkDrafts[current.id] ?? '').trim();
    if (!raw) return;
    const markValue = Number(raw);
    await commitPracticeSelfMark(markValue);
  }, [
    canTutorAssignMark,
    commitPracticeSelfMark,
    current,
    hasTutorOverrideInputMarking,
    practiceOverrideEnabled,
    practiceSelfMarkDrafts,
  ]);

  const adjustPracticeSelfMarkDraft = useCallback(
    (delta: number) => {
      if (!current || !canTutorAssignMark) return;
      const maxMarks = Number(current.marks ?? 0);
      const currentRaw = (practiceSelfMarkDrafts[current.id] ?? '').trim();
      const normalizedAnswer =
        typeof current.answer === 'string' ? current.answer.trim().toLowerCase() : '';
      const normalizedTyped = typedAnswer.trim().toLowerCase();
      const defaultAssigned =
        hasTutorOverrideInputMarking &&
        normalizedAnswer.length > 0 &&
        normalizedTyped.length > 0 &&
        normalizedTyped === normalizedAnswer
          ? maxMarks
          : 0;
      const base =
        currentRaw.length > 0 && Number.isFinite(Number(currentRaw))
          ? Number(currentRaw)
          : (practiceSelfMarks[current.id] ?? defaultAssigned);
      const next = clamp(base + delta, 0, maxMarks);
      setPracticeSelfMarkDrafts((prev) => ({
        ...prev,
        [current.id]: formatMarkValue(next),
      }));
    },
    [
      canTutorAssignMark,
      current,
      hasTutorOverrideInputMarking,
      practiceSelfMarkDrafts,
      practiceSelfMarks,
      typedAnswer,
    ],
  );

  const next = useCallback(async () => {
    if (current && showAnswer && canTutorAssignMark) {
      const raw = (practiceSelfMarkDrafts[current.id] ?? '').trim();
      const parsed = raw.length > 0 && Number.isFinite(Number(raw)) ? Number(raw) : null;
      const maxMarks = Number(current.marks ?? 0);
      const canAutoAssignOnNext =
        !(hasTutorOverrideInputMarking && !practiceOverrideEnabled[current.id]) &&
        parsed != null &&
        parsed >= 0 &&
        parsed <= maxMarks;
      if (canAutoAssignOnNext) {
        await commitPracticeSelfMark(parsed);
      }
    }

    const hasAssignedTutorInputMark =
      isTutorSession &&
      current?.type === 'input' &&
      Number.isFinite(practiceSelfMarks[current.id]);
    if (current && (isTutorSession || effectiveStudentId) && !hasAssignedTutorInputMark) {
      await savePracticeAnswer(
        current,
        selectedIndex,
        resolvePracticeTextAnswer(),
        'next',
        workingSteps,
      );
    }
    setIdx((i) => Math.min(questions.length - 1, i + 1));
  }, [
    canTutorAssignMark,
    commitPracticeSelfMark,
    current,
    effectiveStudentId,
    hasTutorOverrideInputMarking,
    isTutorSession,
    practiceSelfMarks,
    practiceOverrideEnabled,
    practiceSelfMarkDrafts,
    questions.length,
    savePracticeAnswer,
    selectedIndex,
    showAnswer,
    resolvePracticeTextAnswer,
    workingSteps,
  ]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Enter-only shortcut; keep Enter inside editable controls.
      if (e.key !== 'Enter' || e.repeat || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
        return;
      }
      if (e.isComposing) return;
      if (isEditableEventTarget(e.target)) return;

      e.preventDefault();
      if (!showAnswer) {
        if (canRevealAnswerWithEnter) {
          void revealAnswer();
        } else if (!isNextDisabled) {
          void next();
        }
        return;
      }

      if (!isNextDisabled) {
        void next();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [canRevealAnswerWithEnter, isNextDisabled, next, revealAnswer, showAnswer]);

  // Marking logic for short-answer (MathLive) questions
  const trimmedCorrect =
    hasShortAnswerField && typeof current?.answer === 'string'
      ? current.answer.trim()
      : '';

  const trimmedUser = resolvePracticeTextAnswer();

  const aiAdj = current ? aiAdjudication[current.id] : undefined;

  const practiceMarkResult = useMemo((): PracticeMarkResult | null => {
    if (!showAnswer || !current) return null;

    const maxM = Number(current.marks ?? 0);

    if (currentIsMultipleChoice && selectedIndex !== null && current.answer_index != null) {
      const correct = selectedIndex === current.answer_index;
      return {
        awarded: correct ? maxM : 0,
        max: maxM,
        feedback: '',
        verdict: correct ? 'correct' : 'incorrect',
      };
    }

    if (aiAdj) return aiAdj;

    if (hasCanonicalInputAnswer && trimmedUser !== '') {
      const correct = areMathAnswersEquivalent(trimmedUser, trimmedCorrect);
      return {
        awarded: correct ? maxM : 0,
        max: maxM,
        feedback: '',
        verdict: correct ? 'correct' : 'incorrect',
      };
    }

    return null;
  }, [
    showAnswer,
    current,
    currentIsMultipleChoice,
    selectedIndex,
    aiAdj,
    hasCanonicalInputAnswer,
    trimmedUser,
    trimmedCorrect,
  ]);

  // null = don't show; 'unanswered' = blank; 'checking' = AI re-marking.
  const revealedFeedback: MarkVerdict | 'checking' | 'unanswered' | null =
    !showAnswer || !hasShortAnswerField
      ? null
      : aiMarkChecking
        ? 'checking'
        : trimmedUser === ''
          ? 'unanswered'
          : practiceMarkResult?.verdict ??
            (hasAiMarkInput || hasManualInputMarking ? null : 'incorrect');

  const aiCorrectedToCorrect =
    hasCanonicalInputAnswer &&
    showAnswer &&
    !aiMarkChecking &&
    aiAdj?.verdict === 'correct' &&
    trimmedUser !== '' &&
    !areMathAnswersEquivalent(trimmedUser, trimmedCorrect);

  const feedbackLabel = (() => {
    if (revealedFeedback === 'checking' || revealedFeedback === 'unanswered' || !revealedFeedback) {
      return null;
    }
    if (aiCorrectedToCorrect) return 'Correct (accepted by AI marker)';
    if (hasCanonicalInputAnswer && aiAdj && revealedFeedback === 'incorrect') {
      return 'Incorrect (confirmed by AI marker)';
    }
    return verdictLabel(revealedFeedback);
  })();

  const workingAnswerFeedback = useMemo(() => {
    if (!showAnswer || !usesWorkingAnswerInput || !current) return null;
    return evaluateWorkingAnswerFeedback({
      workingSteps,
      finalAnswer: trimmedUser,
      expectedAnswer: trimmedCorrect,
      workedSolution: current.worked_solution,
      finalVerdict: practiceMarkResult?.verdict ?? null,
      finalDetailFeedback: practiceMarkResult?.feedback,
    });
  }, [
    showAnswer,
    usesWorkingAnswerInput,
    current,
    workingSteps,
    trimmedUser,
    trimmedCorrect,
    practiceMarkResult,
  ]);

  const canDisputeMark =
    showAnswer &&
    !isTutorSession &&
    // Disputes end at a tutor, so they need a company behind them.
    hasCompany === true &&
    !!effectiveStudentId &&
    !!practiceMarkResult &&
    practiceMarkResult.verdict !== 'correct' &&
    (currentIsMultipleChoice ? selectedIndex !== null : trimmedUser !== '');

  const currentMarkDispute = current ? markDisputes[current.id] : undefined;

  const buildDisputeAnswerPayload = useCallback(() => {
    if (!current || !practiceMarkResult || !effectiveStudentId) return null;
    let studentAnswer = trimmedUser;
    if (currentIsMultipleChoice && selectedIndex != null) {
      const choices = choicesAsFlatStrings(current.choices);
      studentAnswer =
        choices[selectedIndex] ?? `Option ${letters[selectedIndex] ?? selectedIndex + 1}`;
    }
    if (!studentAnswer.trim()) return null;
    return {
      questionId: current.id,
      studentAnswer: studentAnswer.trim(),
      workingSteps: usesWorkingAnswerInput ? workingSteps : undefined,
      practiceAnswerId: practiceAnswerIdsRef.current.get(current.id),
      originalAwarded: practiceMarkResult.awarded,
      originalMax: practiceMarkResult.max,
      originalModel: aiAdj?.model,
      originalTier: aiAdj?.tier,
    };
  }, [
    aiAdj?.model,
    aiAdj?.tier,
    current,
    currentIsMultipleChoice,
    effectiveStudentId,
    practiceMarkResult,
    selectedIndex,
    trimmedUser,
    usesWorkingAnswerInput,
    workingSteps,
  ]);

  const contestResultToMark = useCallback(
    (
      result: { awarded?: number; maxMarks?: number; feedback?: string; model?: string; tier?: string },
      fallback: PracticeMarkResult,
    ): PracticeMarkResult => {
      const max =
        Number.isFinite(result.maxMarks) && (result.maxMarks ?? 0) > 0
          ? (result.maxMarks as number)
          : fallback.max;
      const awarded = Number.isFinite(result.awarded) ? (result.awarded as number) : fallback.awarded;
      return {
        awarded,
        max,
        feedback: result.feedback ?? fallback.feedback,
        verdict: verdictFromMarks(awarded, max),
        model: result.model ?? fallback.model,
        tier: result.tier ?? fallback.tier,
        partFeedback: fallback.partFeedback,
      };
    },
    [],
  );

  const applyPracticeMark = useCallback(
    (nextMark: PracticeMarkResult) => {
      if (!current) return;
      setAiAdjudication((prev) => ({ ...prev, [current.id]: nextMark }));
      setAttemptMap((prev) => ({
        ...prev,
        [current.id]: {
          isCorrect: nextMark.verdict === 'correct',
          attemptedAt: new Date().toISOString(),
        },
      }));
    },
    [current],
  );

  const runMarkRecheck = useCallback(
    async (comment: string) => {
      const payload = buildDisputeAnswerPayload();
      if (!payload || !current || !practiceMarkResult) {
        throw new Error('Could not re-check this mark.');
      }
      setDisputeSubmitting(true);
      try {
        const result = await invokeContestMark({
          ...payload,
          disputeReason: comment || undefined,
          recheckOnly: true,
        });
        const mark = contestResultToMark(result, practiceMarkResult);
        setMarkDisputes((prev) => ({
          ...prev,
          [current.id]: {
            status: 'recheck_done',
            comment,
            recheckResult: mark,
            message: result.message,
          },
        }));
        return { mark, noEscalation: result.noEscalation };
      } finally {
        setDisputeSubmitting(false);
      }
    },
    [buildDisputeAnswerPayload, contestResultToMark, current, practiceMarkResult],
  );

  const acceptRecheckMark = useCallback(
    async (recheck: PracticeMarkResult) => {
      if (!current) return;
      applyPracticeMark(recheck);
      setMarkDisputes((prev) => ({
        ...prev,
        [current.id]: {
          status: 'accepted',
          message: 'You accepted the re-checked mark.',
          recheckResult: recheck,
        },
      }));
    },
    [applyPracticeMark, current],
  );

  const sendMarkDisputeToTutor = useCallback(
    async (comment: string, recheck: PracticeMarkResult | null) => {
      const payload = buildDisputeAnswerPayload();
      if (!payload || !current) {
        throw new Error('Could not send dispute to tutor.');
      }
      setDisputeSubmitting(true);
      try {
        const result = await invokeContestMark({
          ...payload,
          disputeReason: comment || undefined,
          requestHumanReview: true,
          escalatedAwarded: recheck?.awarded,
          escalatedFeedback: recheck?.feedback,
          escalatedModel: recheck?.model,
          escalatedTier: recheck?.tier,
        });
        setMarkDisputes((prev) => ({
          ...prev,
          [current.id]: {
            status: 'human_review',
            message:
              result.message ?? 'Your dispute has been sent for tutor review.',
            contestId: result.contestId,
            recheckResult: recheck ?? undefined,
            comment,
          },
        }));
      } finally {
        setDisputeSubmitting(false);
      }
    },
    [buildDisputeAnswerPayload, current],
  );

  // Load equations cheat sheet for the current question's category and its ancestors
  useEffect(() => {
    const catId = current?.category_id;
    if (!catId) {
      setEquations([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setEquationsLoading(true);
      try {
        // Build list of this category and its ancestors (to mirror test cheat sheet grouping)
        const relevantCategories: Category[] = [];
        let walker = catById[catId];
        const guard = new Set<string>();
        while (walker && !guard.has(walker.id)) {
          guard.add(walker.id);
          relevantCategories.push(walker);
          if (!walker.parent_category_id) break;
          walker = catById[walker.parent_category_id];
        }

        const categoryIds = relevantCategories.map((c) => c.id);
        if (categoryIds.length === 0) {
          if (!cancelled) {
            setEquations([]);
          }
          return;
        }

        const categoryMap = new Map(relevantCategories.map((c) => [c.id, c.name]));

        const { data, error } = await supabase
          .from('equations')
          .select('id, equation, category_id')
          .in('category_id', categoryIds);
        if (error) throw error;

        if (!cancelled) {
          const withNames: EquationRow[] = (data ?? []).map((eq: any) => ({
            id: eq.id,
            equation: eq.equation,
            category_id: eq.category_id,
            category_name: categoryMap.get(eq.category_id) || 'Unknown Category',
          }));
          setEquations(withNames);
        }
      } catch {
        if (!cancelled) {
          setEquations([]);
        }
      } finally {
        if (!cancelled) {
          setEquationsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [current?.category_id, catById]);

  /* ───────── render ───────── */
  return (
    <Box sx={{ ...wrapperStyle }}>
      {/* Title row with Filters button and Saved Questions button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
        <Typography variant="h5" component="h2">
          Practice
        </Typography>
        <Tooltip title="Filters">
          <IconButton size="small" onClick={openFilters} aria-label="open filters">
            <TuneIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        {!isTutorSession && (
          <>
            {hasCompany === true && (
              <Button
                startIcon={<GavelOutlinedIcon />}
                variant="outlined"
                onClick={() => navigate('/practice/mark-disputes')}
                size="small"
              >
                Mark disputes
              </Button>
            )}
            {/* Saving needs a student record to hang the question off. */}
            {!!effectiveStudentId && (
              <Button
                startIcon={<CollectionsBookmarkIcon />}
                variant="outlined"
                onClick={() => navigate('/practice/saved-questions')}
                size="small"
              >
                Saved Questions
              </Button>
            )}
          </>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Filters Dialog */}
      <Dialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Filters</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Unified tree view: Years → Subjects → Categories */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select years, subjects, and categories
              </Typography>
              {years.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No years available.
                </Typography>
              ) : (
                <RichTreeView
                  items={buildTreeItems()}
                  multiSelect
                  checkboxSelection
                  selectedItems={draftTreeIds}
                  onSelectedItemsChange={handleTreeSelectionChange}
                  sx={{ maxHeight: 500, overflowY: 'auto' }}
                />
              )}
            </Box>

            {/* 🔖 Tags filter */}
            <Autocomplete
              multiple
              options={tagOptions}
              value={draftTags}
              onChange={(_, v) => setDraftTags(v)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Tags"
                  placeholder={
                    tagsLoading
                      ? 'Loading tags…'
                      : tagOptions.length
                        ? 'Select tags'
                        : 'No tags for current selection'
                  }
                />
              )}
              disabled={loading || tagsLoading}
            />
            <Typography variant="caption" color="text.secondary">
              Tags are limited to the selected year/subject/category
              {draftTypes.length === 1 ? ' and type' : ''}.
            </Typography>

            {/* Question type filter */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Stack sx={{ flexShrink: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Question type
                </Typography>
                <ToggleButtonGroup
                  value={draftTypes}
                  onChange={(_, v: Array<'mc' | 'input'> | null) => setDraftTypes(v ?? [])}
                  aria-label="question type filter"
                >
                  <ToggleButton value="mc" aria-label="multiple choice">
                    Multi-choice
                  </ToggleButton>
                  <ToggleButton value="input" aria-label="short answer">
                    Short answer
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <Stack
                direction="row"
                spacing={2}
                alignItems="stretch"
                sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
                flexWrap="wrap"
                useFlexGap
              >
                <Box sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}>
                  <MarksSelect
                    value={draftMarks}
                    onChange={setDraftMarks}
                    disabled={loading}
                  />
                </Box>
                <Box sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}>
                  <DifficultyPillSlider
                    value={draftDifficulty}
                    onChange={setDraftDifficulty}
                    disabled={loading}
                  />
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFiltersOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setDraftTreeIds([]);
              setDraftTags([]);
              setDraftTypes(['mc', 'input']);
              setDraftDifficulty(null);
              setDraftMarks(null);
              setTagOptions(allTags);
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setSelTreeIds(draftTreeIds);
              setSelTypes(draftTypes);
              setSelTags(draftTags);
              setSelDifficulty(draftDifficulty);
              setSelMarks(draftMarks);
              setFiltersOpen(false);
              
              // Update URL with new filters
              const newParams = serializeFiltersToURL(
                draftTreeIds,
                draftTypes,
                draftTags,
                draftDifficulty,
                draftMarks,
              );
              setSearchParams(newParams, { replace: true });

              setLoading(true);
              try {
                await loadQuestionsFrom(
                  draftTreeIds,
                  draftTypes,
                  draftTags,
                  draftDifficulty,
                  draftMarks,
                  true,
                );
              } finally {
                setLoading(false);
              }
            }}
          >
            Apply & Load
          </Button>
        </DialogActions>
      </Dialog>

      {/* Content */}
      {questions.length === 0 ? (
        loading || !hasCompletedQuestionLoad ? (
          <Stack spacing={1} alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Loading questions…
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No questions yet. Adjust filters with the icon, then “Apply & Load”.
          </Typography>
        )
      ) : (
        <Box>
          {current && (
            <Stack direction="row" spacing={1} sx={{ mb: 1.25 }} flexWrap="wrap">
              {(() => {
                const o = getQuestionOrigin(current);
                return (
                  <>
                    <Chip size="small" label={`Year: ${o.yearText}`} />
                    <Chip size="small" label={`Subject: ${o.subjectName}`} />
                    <Chip size="small" label={`Category: ${o.categoryText}`} />
                    <Chip size="small" label={`Marks: ${Number(current.marks ?? 0)}`} />
                    {attemptMap[current.id] && (!currentIsMultipleChoice || showAnswer) && (() => {
                      const a = attemptMap[current.id];
                      const maxM = Number(current.marks ?? 0);
                      const mark = aiAdjudication[current.id];
                      const verdict = mark?.verdict ?? (a.isCorrect ? 'correct' : 'incorrect');
                      const earned = mark?.awarded ?? (a.isCorrect ? maxM : 0);
                      const datePart = a.attemptedAt
                        ? new Date(a.attemptedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                        : null;
                      const tip = [
                        datePart ? `Last attempted: ${datePart}` : 'Previously attempted',
                        `Score: ${earned}/${maxM}`,
                      ].join('  •  ');
                      const Icon =
                        verdict === 'correct'
                          ? CheckCircleIcon
                          : verdict === 'partial'
                            ? WarningAmberIcon
                            : CancelIcon;
                      const iconColor = verdictColor(verdict);
                      return (
                        <Tooltip title={tip} arrow>
                          <Icon sx={{ color: iconColor, fontSize: 20, ml: 0.5 }} />
                        </Tooltip>
                      );
                    })()}
                  </>
                );
              })()}
            </Stack>
          )}
          <Paper variant="outlined" sx={{ p: 2, minHeight: 160, mb: 2 }}>
            {/* Shared prompt */}
            {current?.group_prompt && (
              <Box
                sx={{
                  p: 1.25,
                  mb: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="overline" sx={{ letterSpacing: 0.6 }}>
                  Shared prompt
                </Typography>
                <RichTextWithDefinitions
                  html={current.group_prompt}
                  className="question-body"
                  companyId={companyId}
                />
              </Box>
            )}

            {/* Part label */}
            {current?.part_label && (
              <Typography variant="overline" sx={{ display: 'block', mb: 0.5 }}>
                {current.part_label}
              </Typography>
            )}

            {/* Prompt */}
            {current && (
              <RichTextWithDefinitions
                html={current.prompt}
                className="question-body"
                companyId={companyId}
              />
            )}

            {/* MC options */}
            {currentIsMultipleChoice &&
              (tableMcChoices ? (
                <Box sx={{ mt: 2 }}>
                  <McChoiceTableStudent
                    answerMarks="letter-outline"
                    columns={tableMcChoices.columns}
                    rows={tableMcChoices.rows}
                    selectedIndex={selectedIndex ?? undefined}
                    onSelect={(i) => setSelectedIndex(i)}
                    renderCell={(html) => (
                      <RichTextWithDefinitions html={html} companyId={companyId} />
                    )}
                    revealAnswers={showAnswer}
                    correctIndex={current.answer_index}
                  />
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {asChoiceArray(current.choices).map((choice, i) => {
                    const isCorrect =
                      current.answer_index != null && i === current.answer_index;
                    const isSelected = selectedIndex === i;
                    const isWrongSelection =
                      showAnswer && isSelected && !isCorrect;
                    const borderColor =
                      isCorrect && showAnswer
                        ? 'success.main'
                        : isWrongSelection
                          ? 'error.main'
                          : isSelected
                            ? 'primary.main'
                            : 'divider';

                    return (
                      <Paper
                        key={i}
                        variant="outlined"
                        onClick={() => setSelectedIndex(i)}
                        sx={{
                          p: 1.25,
                          mb: 1,
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          cursor: 'pointer',
                          borderColor,
                          borderWidth: (isCorrect && showAnswer) || isWrongSelection ? 2 : 1,
                          bgcolor:
                            isSelected && !showAnswer ? 'action.hover' : 'background.paper',
                          transition:
                            'background 120ms, border-color 120ms, border-width 120ms',
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '999px',
                            border: '1px solid',
                            borderColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            flex: '0 0 auto',
                            mt: 0.25,
                          }}
                        >
                          {letters[i] ?? String(i + 1)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <RichTextWithDefinitions
                            html={choice}
                            companyId={companyId}
                          />
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              ))}

            {/* Short-answer input box */}
            {hasShortAnswerField && (
              <Box sx={{ mt: 2 }}>
                <Box>
                  {usesWorkingAnswerInput ? (
                    <WorkingAnswerInput
                      workingSteps={workingSteps}
                      draft={workingDraft}
                      finalAnswer={finalAnswer}
                      onDraftChange={setWorkingDraft}
                      onCommitStep={handleCommitWorkingStep}
                      onFinalAnswerChange={handleFinalAnswerChange}
                      disabled={showAnswer}
                      unit={(current.unit ?? '').trim() || undefined}
                      answerFeedback={showAnswer ? workingAnswerFeedback : null}
                    />
                  ) : (
                    <QuestionInput
                      question={current as any}
                      value={typedAnswer}
                      onChange={setTypedAnswer}
                      unit={(current.unit ?? '').trim() || undefined}
                      useRichTextEditor={hasManualInputMarking || hasAiMarkInput}
                    />
                  )}
                </Box>

                {revealedFeedback === 'checking' && (
                  <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary' }}>
                    Checking your answer{hasAiMarkInput || hasCanonicalInputAnswer ? ' with AI' : ''}…
                  </Typography>
                )}

                {feedbackLabel && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.75,
                      fontWeight: 600,
                      color: verdictColor(revealedFeedback as MarkVerdict),
                    }}
                  >
                    {feedbackLabel}
                    {practiceMarkResult && practiceMarkResult.max > 0 && (
                      <> · {formatMarkFraction(practiceMarkResult.awarded, practiceMarkResult.max)}</>
                    )}
                  </Typography>
                )}

                {revealedFeedback === 'unanswered' && (
                  <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary' }}>
                    No answer entered.
                  </Typography>
                )}
                {showAnswer && isTutorSession && canTutorAssignMark && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {(() => {
                      const maxMarks = Number(current.marks ?? 0);
                      const isOverrideMode = hasTutorOverrideInputMarking;
                      const overrideEnabled = !!practiceOverrideEnabled[current.id];
                      const autoDerivedMark = practiceMarkResult?.awarded ?? 0;
                      const raw = (practiceSelfMarkDrafts[current.id] ?? '').trim();
                      const parsed =
                        raw.length > 0 && Number.isFinite(Number(raw)) ? Number(raw) : null;
                      const assigned =
                        practiceSelfMarks[current.id] ??
                        (parsed != null ? clamp(parsed, 0, maxMarks) : autoDerivedMark);
                      const displayMark = raw.length > 0 ? raw : formatMarkValue(assigned);
                      const canAssign =
                        parsed != null &&
                        parsed >= 0 &&
                        parsed <= maxMarks;
                      const canEdit = !isOverrideMode || overrideEnabled;
                      return (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden',
                            width: 'fit-content',
                            bgcolor: 'background.paper',
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            sx={{
                              px: 1,
                              py: 0.4,
                              minWidth: 102,
                              gap: 0.5,
                              opacity: canEdit ? 1 : 0.5,
                              pointerEvents: canEdit ? 'auto' : 'none',
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 64,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.25,
                                color: 'text.primary',
                              }}
                            >
                              <Box
                                component="input"
                                value={displayMark}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const nextRaw = e.target.value.trim();
                                  if (nextRaw === '' || /^\d+(\.\d{0,1})?$/.test(nextRaw)) {
                                    setPracticeSelfMarkDrafts((prev) => ({
                                      ...prev,
                                      [current.id]: nextRaw,
                                    }));
                                  }
                                }}
                                onBlur={() => {
                                  const nextRaw = (practiceSelfMarkDrafts[current.id] ?? '').trim();
                                  if (!nextRaw) return;
                                  const nextVal = Number(nextRaw);
                                  if (!Number.isFinite(nextVal)) return;
                                  const clamped = clamp(nextVal, 0, maxMarks);
                                  setPracticeSelfMarkDrafts((prev) => ({
                                    ...prev,
                                    [current.id]: formatMarkValue(clamped),
                                  }));
                                }}
                                sx={{
                                  width: `${Math.max(displayMark.length, 1)}ch`,
                                  minWidth: '2ch',
                                  border: 0,
                                  outline: 0,
                                  p: 0,
                                  m: 0,
                                  display: 'block',
                                  background: 'transparent',
                                  textAlign: 'right',
                                  fontWeight: 700,
                                  fontSize: 16,
                                  lineHeight: '20px',
                                  height: '20px',
                                  color: 'inherit',
                                  verticalAlign: 'middle',
                                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                }}
                              />
                              <Box sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1 }}>
                                / {formatMarkValue(maxMarks)}
                              </Box>
                            </Box>
                            <Stack sx={{ minWidth: 18 }}>
                              <IconButton
                                size="small"
                                onClick={() => adjustPracticeSelfMarkDraft(0.5)}
                                sx={{
                                  borderRadius: 0,
                                  height: 12,
                                  p: 0,
                                  color: 'text.disabled',
                                  '&:hover': { backgroundColor: 'transparent', color: 'text.secondary' },
                                }}
                              >
                                <KeyboardArrowUpIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => adjustPracticeSelfMarkDraft(-0.5)}
                                sx={{
                                  borderRadius: 0,
                                  height: 12,
                                  p: 0,
                                  color: 'text.disabled',
                                  '&:hover': { backgroundColor: 'transparent', color: 'text.secondary' },
                                }}
                              >
                                <KeyboardArrowDownIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Stack>
                          </Stack>
                          <Divider orientation="vertical" flexItem />
                          {isOverrideMode && !overrideEnabled ? (
                            <Button
                              onClick={() =>
                                setPracticeOverrideEnabled((prev) => ({
                                  ...prev,
                                  [current.id]: true,
                                }))
                              }
                              sx={{
                                borderRadius: 0,
                                px: 1.4,
                                py: 0.45,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: 13,
                                minWidth: 108,
                                color: 'grey.700',
                                bgcolor: 'transparent',
                                '&:hover': { bgcolor: 'grey.100' },
                              }}
                            >
                              Enable override
                            </Button>
                          ) : (
                            <Button
                              onClick={assignPracticeSelfMark}
                              disabled={!canAssign}
                              sx={{
                                borderRadius: 0,
                                px: 1.4,
                                py: 0.45,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: 13,
                                minWidth: 108,
                                color: 'grey.700',
                                bgcolor: 'transparent',
                                '&:hover': { bgcolor: 'grey.100' },
                                '&.Mui-disabled': {
                                  color: 'grey.500',
                                  bgcolor: 'transparent',
                                },
                              }}
                            >
                              Submit marks
                            </Button>
                          )}
                        </Box>
                      );
                    })()}
                    {practiceSelfMarks[current.id] != null && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Assigned: {formatMarkValue(practiceSelfMarks[current.id])} / {formatMarkValue(Number(current.marks ?? 0))}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>
            )}

            {/* (Short-answer solution still shown in AnswerBox when revealed) */}
          </Paper>

          {/* Separate Answer Box */}
          {showAnswer && current && <AnswerBox q={current} markResult={practiceMarkResult} />}

          {canDisputeMark && practiceMarkResult && (
            <MarkDisputePanel
              markResult={practiceMarkResult}
              dispute={currentMarkDispute ?? null}
              submitting={disputeSubmitting}
              onRecheck={runMarkRecheck}
              onAcceptRecheck={acceptRecheckMark}
              onSendToTutor={sendMarkDisputeToTutor}
            />
          )}

          <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
            <Button variant="outlined" onClick={prev} disabled={idx === 0}>
              Previous
            </Button>

            <Stack direction="row" gap={1} alignItems="center">
              <Button
                variant="outlined"
                disabled={!current || (equations.length === 0 && !equationsLoading)}
                onClick={() => {
                  blurActiveElement();
                  setShowCheatSheetDialog(true);
                }}
                sx={{ minWidth: 150 }}
              >
                Show Cheat Sheet
              </Button>

              <Button
                variant="outlined"
                color={showAnswer ? 'primary' : 'inherit'}
                onClick={toggleShowAnswer}
                disabled={!current || aiMarkChecking}
                sx={{
                  borderColor: showAnswer ? 'primary.main' : 'grey.600',
                  '&:hover': { borderWidth: showAnswer ? 2 : 1 },
                }}
              >
                {showAnswer ? 'Hide Answer' : 'Show Answer'}
              </Button>
            </Stack>

            <Stack direction="row" gap={1} alignItems="center">
              <QuestionActionButtons
                questionId={current?.id}
                studentId={effectiveStudentId}
                companyId={companyId}
                showSave={!isTutorSession && !!effectiveStudentId}
                showFlag
              />

              <Button
                variant="contained"
                onClick={next}
                disabled={isNextDisabled}
              >
                {idx === questions.length - 1 && loadingMore
                  ? 'Loading…'
                  : idx === questions.length - 1 && !hasMore
                    ? 'End'
                    : 'Next'}
              </Button>
            </Stack>
          </Stack>

          {/* Load-more affordance + spinner */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
            {loadingMore && <CircularProgress size={22} />}
            {!loadingMore && idx === questions.length - 1 && hasMore && (
              <Button onClick={loadMore} size="small" sx={{ mt: 0.5 }}>
                Load more questions
              </Button>
            )}
            {!loadingMore && idx === questions.length - 1 && !hasMore && (
              <Typography variant="caption" color="text.secondary">
                You’ve reached the end of what matches your filters.
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Cheat Sheet Dialog (matches TestPage style) */}
      <Dialog
        open={showCheatSheetDialog}
        onClose={() => setShowCheatSheetDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Cheat Sheet</DialogTitle>
        <DialogContent dividers>
          {equationsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : equations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No equations available for this question.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {equations.map((eq) => (
                <Paper
                  key={eq.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderColor: 'divider',
                  }}
                >
                  <RichText html={eq.equation} />
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCheatSheetDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PracticePage;
